import React, { useState } from "react";
import { User, Mail, KeyRound, Trash2, Check, X, Eye, EyeOff, ShieldAlert, Camera } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Panel, Overline } from "../common/Panel";
import { toast } from "sonner";

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[var(--accent)] transition-colors";

function PasswordInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={inputCls + " pr-10"}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--t2)] hover:text-[var(--t1)]"
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

function DeleteModal({ email, onConfirm, onCancel }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (confirm !== "DELETE") { toast.error('Type DELETE to confirm'); return; }
    if (!password) { toast.error('Enter your current password'); return; }
    setBusy(true);
    try {
      await onConfirm(password);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-[var(--surface)] border border-[var(--danger)]/50 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--danger)]/15 flex items-center justify-center">
            <ShieldAlert size={20} className="text-[var(--danger)]" />
          </div>
          <div>
            <div className="font-heading font-bold">Delete Account</div>
            <div className="text-xs text-[var(--t2)]">This action is permanent and cannot be undone.</div>
          </div>
        </div>
        <p className="text-sm text-[var(--t2)]">Account <span className="font-mono text-[var(--t1)]">{email}</span> and all associated data will be permanently deleted.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--t2)]">Current password</label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" testid="delete-password" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--t2)]">Type DELETE to confirm</label>
            <input
              data-testid="delete-confirm-text"
              className={inputCls}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-md border border-[var(--bd)] text-sm font-medium hover:border-[var(--bd-strong)] transition-colors">Cancel</button>
            <button
              data-testid="delete-account-confirm"
              type="submit"
              disabled={busy || confirm !== "DELETE" || !password}
              className="flex-1 py-2.5 rounded-md bg-[var(--danger)] text-white text-sm font-medium disabled:opacity-40 hover:brightness-110 transition-all active:scale-[0.98]"
            >
              {busy ? "Deleting…" : "Delete Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, updateDisplayName, changePassword, deleteAccount, logout } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [nameLoading, setNameLoading] = useState(false);

  const [curPass, setCurPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  const [showDelete, setShowDelete] = useState(false);

  const initials = (user?.name || user?.email || "U").trim().slice(0, 2).toUpperCase();
  const joinedDate = new Date().getFullYear();

  const saveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setNameLoading(true);
    try {
      await updateDisplayName(name.trim());
      toast.success("Display name updated");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setNameLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPass.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPass !== confirmPass) { toast.error("Passwords don't match"); return; }
    setPassLoading(true);
    try {
      await changePassword(curPass, newPass);
      toast.success("Password changed successfully");
      setCurPass(""); setNewPass(""); setConfirmPass("");
    } catch (err) {
      toast.error(err.code === "auth/wrong-password" ? "Current password is incorrect" : err.message);
    } finally {
      setPassLoading(false);
    }
  };

  const handleDelete = async (password) => {
    try {
      await deleteAccount(password);
      toast.success("Account deleted");
      navigate("/");
    } catch (err) {
      toast.error(err.code === "auth/wrong-password" ? "Incorrect password" : err.message);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Overline>Account</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">My Profile</h2>
      </div>

      {/* Profile card */}
      <Panel className="p-6">
        <div className="flex items-center gap-5">
          <div className="relative shrink-0">
            <img
              src={user?.photoURL || "/assets/profile.png"}
              alt="Profile"
              className="w-20 h-20 rounded-full object-cover object-top border-2 border-[var(--accent)]"
              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
            />
            <div
              className="w-20 h-20 rounded-full bg-[var(--accent)] text-black font-black text-2xl items-center justify-center hidden"
              aria-hidden
            >
              {initials}
            </div>
            <div className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-[var(--surface2)] border border-[var(--bd)] flex items-center justify-center">
              <Camera size={11} className="text-[var(--t2)]" />
            </div>
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-xl">{user?.name || "User"}</div>
            <div className="font-mono text-sm text-[var(--t2)] truncate">{user?.email}</div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] border border-[var(--accent)]/30">
                Authenticated
              </span>
              <span className="text-[10px] font-mono text-[var(--t2)]">Member since {joinedDate}</span>
            </div>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[var(--bd)] grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-[var(--accent-soft)] flex items-center justify-center">
              <User size={14} className="text-[var(--accent)]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--t2)]">Display Name</div>
              <div className="font-mono">{user?.name || "-"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-[var(--accent-soft)] flex items-center justify-center">
              <Mail size={14} className="text-[var(--accent)]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--t2)]">Email Address</div>
              <div className="font-mono truncate">{user?.email || "-"}</div>
            </div>
          </div>
        </div>
      </Panel>

      {/* Edit display name */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <User size={16} className="text-[var(--accent)]" />
          <Overline>Edit Display Name</Overline>
        </div>
        <form onSubmit={saveName} className="flex gap-3">
          <input
            data-testid="profile-name-input"
            className={inputCls + " flex-1"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your display name"
          />
          <button
            data-testid="profile-name-save"
            type="submit"
            disabled={nameLoading || name.trim() === (user?.name || "")}
            className="px-4 py-2.5 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform disabled:opacity-40 flex items-center gap-2 shrink-0"
          >
            <Check size={14} /> {nameLoading ? "Saving…" : "Save"}
          </button>
        </form>
        <p className="text-xs text-[var(--t2)] font-mono">Email address cannot be changed here - contact admin.</p>
      </Panel>

      {/* Change password */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound size={16} className="text-[var(--accent)]" />
          <Overline>Change Password</Overline>
        </div>
        <form onSubmit={savePassword} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--t2)]">Current password</label>
            <PasswordInput value={curPass} onChange={(e) => setCurPass(e.target.value)} placeholder="••••••••" testid="profile-cur-pass" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--t2)]">New password (min 6 chars)</label>
            <PasswordInput value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="••••••••" testid="profile-new-pass" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--t2)]">Confirm new password</label>
            <PasswordInput value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} placeholder="••••••••" testid="profile-confirm-pass" />
          </div>
          {newPass && confirmPass && newPass !== confirmPass && (
            <p className="text-xs text-[var(--danger)] font-mono flex items-center gap-1"><X size={12} /> Passwords don't match</p>
          )}
          <button
            data-testid="profile-pass-save"
            type="submit"
            disabled={passLoading || !curPass || !newPass || !confirmPass || newPass !== confirmPass}
            className="px-4 py-2.5 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform disabled:opacity-40 flex items-center gap-2"
          >
            <KeyRound size={14} /> {passLoading ? "Updating…" : "Update Password"}
          </button>
        </form>
      </Panel>

      {/* Danger zone */}
      <Panel className="p-6 space-y-3 border-[var(--danger)]/30">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-[var(--danger)]" />
          <Overline className="text-[var(--danger)]">Danger Zone</Overline>
        </div>
        <p className="text-sm text-[var(--t2)]">
          Permanently delete your account and remove access to the Campus Energy Monitor dashboard.
          This action <strong className="text-[var(--t1)]">cannot be undone</strong>.
        </p>
        <button
          data-testid="delete-account-btn"
          onClick={() => setShowDelete(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[var(--danger)]/50 text-[var(--danger)] text-sm font-medium hover:bg-[var(--danger)]/10 transition-colors"
        >
          <Trash2 size={15} /> Delete My Account
        </button>
      </Panel>

      {showDelete && (
        <DeleteModal
          email={user?.email}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
}
